/** Sector tags offered even before any employee uses them. */
export const DEFAULT_SECTOR_TAGS: readonly string[] = ['Cozinha', 'Salão', 'Limpeza', 'Apoio Operacional'];

export const SECTOR_TAG_MAX_LENGTH = 40;

const tagKey = (tag: string): string => tag.toLocaleLowerCase('pt-BR');

/** Trims and collapses inner spaces ("  Bar   e Caixa " -> "Bar e Caixa"). */
export function normalizeSectorTag(raw: string): string {
  return (raw ?? '').replace(/\s+/g, ' ').trim();
}

/** True when both tags are the same ignoring case and extra spaces. */
export function sameSectorTag(a: string, b: string): boolean {
  return tagKey(normalizeSectorTag(a)) === tagKey(normalizeSectorTag(b));
}

/**
 * Catalog shown in the batch bar, in the employee modal and in the sector filter:
 * default tags first, then every distinct tag already used or created, sorted in pt-BR.
 * Duplicates are removed ignoring case; the first spelling found wins.
 */
export function buildSectorTagCatalog(
  employeeTags: readonly (readonly string[] | undefined)[],
  customTags: readonly string[] = []
): string[] {
  const seen = new Set<string>();
  const defaults: string[] = [];
  const extras: string[] = [];

  const push = (raw: string, target: string[]): void => {
    const tag = normalizeSectorTag(raw);
    if (!tag || tag.length > SECTOR_TAG_MAX_LENGTH) return;
    const key = tagKey(tag);
    if (seen.has(key)) return;
    seen.add(key);
    target.push(tag);
  };

  DEFAULT_SECTOR_TAGS.forEach((tag) => push(tag, defaults));
  for (const tags of employeeTags) {
    (tags ?? []).forEach((tag) => push(tag, extras));
  }
  customTags.forEach((tag) => push(tag, extras));

  extras.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
  return [...defaults, ...extras];
}

export type SectorTagValidation =
  | { ok: true; tag: string }
  | { ok: false; reason: 'EMPTY' | 'TOO_LONG' | 'DUPLICATE'; existing?: string };

/** Checks a tag typed by the user against the catalog before adding it. */
export function validateNewSectorTag(raw: string, catalog: readonly string[]): SectorTagValidation {
  const tag = normalizeSectorTag(raw);
  if (!tag) return { ok: false, reason: 'EMPTY' };
  if (tag.length > SECTOR_TAG_MAX_LENGTH) return { ok: false, reason: 'TOO_LONG' };
  const existing = catalog.find((item) => sameSectorTag(item, tag));
  if (existing) return { ok: false, reason: 'DUPLICATE', existing };
  return { ok: true, tag };
}

/** Human message for a rejected tag. */
export function sectorTagValidationMessage(result: SectorTagValidation): string {
  if (result.ok) return '';
  switch (result.reason) {
    case 'EMPTY':
      return 'Digite o nome da tag antes de adicionar.';
    case 'TOO_LONG':
      return `A tag pode ter no máximo ${SECTOR_TAG_MAX_LENGTH} caracteres.`;
    case 'DUPLICATE':
      return `A tag "${result.existing}" já existe.`;
  }
}
